import { Component, computed, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BluetoothDevice, Monitor, UsbDevice } from '../../../../../Models/avyna/agent-device.model';
import { AvContentContainerComponent } from '../../../../Shared/Components/av-content-container/av-content-container.component';
import { AvTableCard } from '../../../../Shared/Components/av-table-card/av-table-card.component';
import { AvTableColumn, AvTableRow } from '../../../../Shared/Interfaces/av-table-card.interface';
import { ShowDeviceFacade } from '../../../../../Facades/Intune/show-device.facade';

@Component({
  selector: 'app-connected-devices',
  standalone: true,
  imports: [AvContentContainerComponent, AvTableCard],
  templateUrl: './connected-devices.component.html',
  styleUrls: ['./connected-devices.component.scss'],
})
export class ConnectedDevicesComponent {
  private readonly _facade = inject(ShowDeviceFacade);
  private readonly translate = inject(TranslateService);

  readonly device = this._facade.device;

  readonly usbDeviceColumns: AvTableColumn[] = [
    { key: 'name', label: 'app.device.connected_devices.columns.name', sortable: true },
    { key: 'deviceClass', label: 'app.device.connected_devices.columns.class', sortable: true },
    { key: 'deviceId', label: 'app.device.connected_devices.columns.device_id', sortable: true },
  ];

  readonly monitorColumns: AvTableColumn[] = [
    { key: 'monitorId', label: 'app.device.connected_devices.columns.monitor_id', sortable: true },
    { key: 'resolution', label: 'app.device.connected_devices.columns.resolution', sortable: true },
    { key: 'monitorType', label: 'app.device.connected_devices.columns.monitor_type', sortable: true },
  ];

  readonly bluetoothColumns: AvTableColumn[] = [
    { key: 'deviceId', label: 'app.device.connected_devices.columns.device_id', sortable: true },
    { key: 'name', label: 'app.device.connected_devices.columns.name', sortable: true },
    { key: 'errorCode', label: 'app.device.connected_devices.columns.error_code', sortable: true },
    { key: 'manufacturer', label: 'app.device.connected_devices.columns.manufacturer', sortable: true },
  ];

  readonly usbDeviceRows = computed<AvTableRow[]>(() => {
    const devices = this.device()?.agent?.connected_devices?.usb_devices;
    if (!Array.isArray(devices)) return [];

    return devices.map((device: UsbDevice) => ({
      name: this.fallbackString(device?.name),
      deviceClass: this.fallbackString(device?.class),
      deviceId: this.fallbackString(device?.device_id),
    }));
  });

  readonly monitorRows = computed<AvTableRow[]>(() => {
    const monitors = this.device()?.agent?.connected_devices?.monitors;
    if (!Array.isArray(monitors)) return [];

    return monitors.map((monitor: Monitor) => ({
      monitorId: this.fallbackString(monitor?.monitor_id),
      resolution: this.fallbackString(monitor?.resolution),
      monitorType: this.fallbackString(monitor?.monitor_type),
    }));
  });

  readonly bluetoothRows = computed<AvTableRow[]>(() => {
    const devices = this.device()?.agent?.connected_devices?.bluetooth_devices;
    if (!Array.isArray(devices)) return [];

    return devices.map((device: BluetoothDevice) => ({
      name: this.fallbackString(device?.name),
      deviceId: this.fallbackString(device?.device_id),
      errorCode: this.fallbackString(device?.error_code),
      manufacturer: this.fallbackString(device?.manufacturer),
    }));
  });

  private fallbackString(value?: string | null): string {
    if (value && value.trim().length > 0) return value;
    return this.translate.instant('shared.general.labels.na');
  }
}
